import {useCallback, useEffect, useRef} from 'react';
import {useState} from 'react';
import styles from './PaymentForm.module.scss';
import api from 'api';
import {get} from 'lodash';
import Spinner from 'components/Spinner';
import Logo from 'components/Logo';
import StripeForm, {PaymentDetails} from './components';
import {loadStripe} from '@stripe/stripe-js';
import {Elements} from '@stripe/react-stripe-js';
import HeartlandForm from './components/HeartlandForm';
import PaymentSuccess from './components/PaymentSuccess';
import {MailIcon} from './icons';
import Typography from 'components/Typography';

type Props = {
  id: string;
};

const PaymentForm = ({id}: Props) => {
  const [fetching, setFetching] = useState(true);
  const [success, setSuccess] = useState(false);
  const [datas, setData] = useState({});
  const stripePromise = useRef<any>(null);

  const fetchData = useCallback(() => {
    api
      .get(`/payment/links/${id}/`)
      .then(({data}) => {
        const isPaidAll = get(data, 'request_drugs', []).every(
          (item: any) => item.is_paid
        );
        const noAmountDue = get(data, 'request_drugs', []).every(
          (item: any) => Number(item.amount_due) === 0
        );
        setSuccess(isPaidAll && noAmountDue);
        if (get(data, 'gateway_type', '') === 'stripe') {
          stripePromise.current = loadStripe(
            get(data, 'gateway_public_key', '')
          );
        }
        setData(data);
      })
      .finally(() => {
        setFetching(false);
      });
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const renderForm = useCallback(() => {
    if (get(datas, 'gateway_type') === 'stripe') {
      const copay_items = get(datas, 'request_drugs', []).map((item: any) => ({
        id: item.id,
        amount_due: item.amount_due,
      }));
      return (
        <Elements stripe={stripePromise.current}>
          <StripeForm
            onSuccess={() => setSuccess(true)}
            paymentId={id}
            copayItems={copay_items}
          />
        </Elements>
      );
    }
    return (
      <HeartlandForm
        onSuccess={() => setSuccess(true)}
        heartlandPublicKey={get(datas, 'gateway_public_key', '')}
        paymentId={id}
        amountDue={get(datas, 'request.amount_due', '')}
      />
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datas, id]);

  if (fetching) {
    return (
      <div className={styles.spinner}>
        <Spinner />
      </div>
    );
  }

  if (success) {
    return <PaymentSuccess datas={datas} />;
  }
  return (
    <>
      <div className={styles.brandContainer}>
        <div className={styles.brand}>
          <div className={styles.logo}>
            <Logo style={{width: 75, height: 75, filter: 'invert(0)'}} />
          </div>
          <span className={styles.brandName}>Magic</span>
        </div>
      </div>

      <div className={styles.container}>
        <PaymentDetails datas={datas} />
        {renderForm()}
      </div>
      <div className={styles.footer}>
        <div className={styles.mailIconBox}>
          <MailIcon className={styles.mailIcon} />
        </div>
        <div className={styles.helpContainer}>
          <Typography variant="h4">Need help? </Typography>
          <Typography variant="label">
            If you have any questions about this payment or encounter any
            issues, please contact our support team.
          </Typography>
          <a
            href="mailto:arthur@triangleempire.com"
            target="_blank"
            rel="noreferrer"
            className={styles.button}
          >
            Contact Support
          </a>
        </div>
      </div>
    </>
  );
};

export default PaymentForm;
